import type { IObservableArray } from 'mobx';
import { useLocalObservable } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Conversion,
  IConversionFilters,
  Targeting,
  TAttributionModel,
} from '@feathr/blackbox';
import type { ISorted } from '@feathr/components';
import { toast } from '@feathr/components';
import { addDayToDate } from '@feathr/hooks';
import type { IRachisMessage, ListResponse } from '@feathr/rachis';
import { useStore } from '@feathr/report_components/state';

import {
  getDefaultColumnIds,
  getProductConfig,
  getProductType,
} from './ConversionTableCard.config';
import type { ICategoryOption, TProductModel } from './ConversionTableCard.types';
import { createDateFilters, loadPresets, savePresets } from './ConversionTableCard.utils';

/**
 * Props for the useConversionsData hook.
 */
export interface IUseConversionsDataProps {
  model: TProductModel;
  start: string;
  end: string;
  attributionModel: TAttributionModel;
  targetings: IObservableArray<Targeting>;
  localUrl?: (url: string) => string;
}

export interface IUseConversionsDataReturn {
  conversions: ListResponse<Conversion>;
  isLoading: boolean;
  filters: IConversionFilters;
  pagination: { page: number; page_size: number };
  sort: ISorted[];
  columnIds: string[];
  currentAttributionModel: TAttributionModel;
  handleCategoryChange: (categories?: ICategoryOption[]) => void;
  handlePageChange: (page: number) => void;
  handlePageSizeChange: (pageSize: number, page: number) => void;
  handleSortChange: (sort: ISorted[]) => void;
  handleColumnIdsChange: (columnIds: string[]) => void;
  handleExport: (email: string) => Promise<IRachisMessage>;
  handleRerun: () => Promise<void>;
}

/**
 * Hook for managing conversion data, pagination, sorting, and filtering.
 *
 * @param props - Configuration props
 * @returns Object with conversions data and handlers
 *
 * @example
 * const {
 *   conversions,
 *   isLoading,
 *   handlePageChange,
 *   handleSortChange,
 * } = useConversionsData({ model, start, end, attributionModel, targetings });
 */
export function useConversionsData({
  model,
  start,
  end,
  attributionModel,
  targetings,
  localUrl,
}: IUseConversionsDataProps): IUseConversionsDataReturn {
  const { Conversions, Events, Flights, Campaigns } = useStore();
  const { t } = useTranslation();

  const defaultColumnIds = getDefaultColumnIds(model, targetings.length > 0, Boolean(localUrl));
  const presets = loadPresets(model.id);
  const initialSort = presets?.sort ?? [{ id: 'd_c' }];
  const initialColumnIds = presets?.columnIds ?? defaultColumnIds;

  const getCollection = useCallback(() => {
    const productType = getProductType(model);
    switch (productType) {
      case 'event':
        return Events;

      case 'flight':
        return Flights;

      case 'campaign':
        return Campaigns;
    }
  }, [model, Events, Flights, Campaigns]);

  const fetchConversions = useCallback(
    (
      filters: IConversionFilters,
      pagination: { page: number; page_size: number },
      sort: ISorted[],
      attrModel: TAttributionModel,
    ): ListResponse<Conversion> => {
      const ordering = [`${sort[0]?.desc ? '-' : ''}${sort[0]?.id ?? 'd_c'}`];
      const params = {
        filters: { ...filters, attribution_model: attrModel },
        pagination,
        ordering,
      };
      const collection = getCollection();
      return collection.listConversions(model.id, params, Conversions);
    },
    [model.id, getCollection, Conversions],
  );

  const state = useLocalObservable(() => ({
    conversions: fetchConversions(
      createDateFilters(start, end),
      { page: 0, page_size: 10 },
      initialSort,
      attributionModel,
    ),
    filters: createDateFilters(start, end) as IConversionFilters,
    pagination: { page: 0, page_size: 10 },
    sort: initialSort,
    columnIds: initialColumnIds,
    attributionModel,

    setConversions(conversions: ListResponse<Conversion>): void {
      this.conversions = conversions;
    },
    setFilters(filters: IConversionFilters): void {
      this.filters = filters;
    },
    setPagination(pagination: { page: number; page_size: number }): void {
      this.pagination = pagination;
    },
    setSort(sort: ISorted[]): void {
      this.sort = sort;
    },
    setColumnIds(columnIds: string[]): void {
      this.columnIds = columnIds;
    },
    setAttributionModel(attrModel: TAttributionModel): void {
      this.attributionModel = attrModel;
    },
  }));

  useEffect(() => {
    if (attributionModel !== state.attributionModel) {
      state.setAttributionModel(attributionModel);
      state.setConversions(
        fetchConversions(state.filters, state.pagination, state.sort, attributionModel),
      );
      savePresets(model.id, state.sort, state.columnIds);
    }
  }, [attributionModel, fetchConversions, model.id, state]);

  useEffect(() => {
    const updatedFilters = { ...state.filters, ...createDateFilters(start, end) };
    state.setFilters(updatedFilters);
  }, [start, end, state]);

  const handleCategoryChange = useCallback(
    (categories?: ICategoryOption[]): void => {
      const newFilters = { ...state.filters };
      if (categories?.length) {
        newFilters.category__in = categories.map((c) => c.value);
      } else {
        delete newFilters.category__in;
      }
      state.setFilters(newFilters);
      state.setPagination({ page: 0, page_size: state.pagination.page_size });
      state.setConversions(
        fetchConversions(
          newFilters,
          { page: 0, page_size: state.pagination.page_size },
          state.sort,
          state.attributionModel,
        ),
      );
    },
    [state, fetchConversions],
  );

  const handlePageChange = useCallback(
    (page: number): void => {
      state.setPagination({ ...state.pagination, page });
      state.setConversions(
        fetchConversions(
          state.filters,
          { ...state.pagination, page },
          state.sort,
          state.attributionModel,
        ),
      );
    },
    [state, fetchConversions],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number, page: number): void => {
      state.setPagination({ page, page_size: pageSize });
      state.setConversions(
        fetchConversions(
          state.filters,
          { page, page_size: pageSize },
          state.sort,
          state.attributionModel,
        ),
      );
    },
    [state, fetchConversions],
  );

  const handleSortChange = useCallback(
    (sort: ISorted[]): void => {
      state.setSort(sort);
      savePresets(model.id, sort, state.columnIds);
      state.setConversions(
        fetchConversions(state.filters, state.pagination, sort, state.attributionModel),
      );
    },
    [state, model.id, fetchConversions],
  );

  const handleColumnIdsChange = useCallback(
    (columnIds: string[]): void => {
      state.setColumnIds(columnIds);
      savePresets(model.id, state.sort, columnIds);
    },
    [state, model.id],
  );

  const handleExport = useCallback(
    async (email: string): Promise<IRachisMessage> => {
      const config = getProductConfig(model);
      const params = new URLSearchParams({
        [config.apiParamKey]: model.id,
        attribution_model: state.attributionModel,
        start,
        end: addDayToDate(end),
        email,
      });
      return Conversions.export(params);
    },
    [model, state.attributionModel, start, end, Conversions],
  );

  const handleRerun = useCallback(async (): Promise<void> => {
    const config = getProductConfig(model);
    const params = new URLSearchParams({ [config.apiParamKey]: model.id });
    try {
      await Conversions.rerun(params);
      model.set({ rerun: true });
    } catch (e) {
      toast(t('Something went wrong when trying to recalculate conversions.'), { type: 'error' });
      throw e;
    }
  }, [model, Conversions, t]);

  return {
    conversions: state.conversions,
    isLoading: state.conversions.isPending,
    filters: state.filters,
    pagination: state.pagination,
    sort: state.sort,
    columnIds: state.columnIds,
    currentAttributionModel: state.attributionModel,
    handleCategoryChange,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleColumnIdsChange,
    handleExport,
    handleRerun,
  };
}

/**
 * Hook for loading category options for the category filter.
 *
 * @param model - The model (Event, Flight, or Campaign)
 * @param attributionModel - Attribution model for the query
 * @param filters - Current conversion filters
 * @returns Object with loadOptions function
 */
export function useCategoryOptions(
  model: TProductModel,
  attributionModel: TAttributionModel,
  filters: IConversionFilters,
): { loadOptions: () => Promise<ICategoryOption[]> } {
  const { Events, Flights, Campaigns } = useStore();

  const loadOptions = useCallback(async (): Promise<ICategoryOption[]> => {
    const productType = getProductType(model);
    let collection;
    if (productType === 'event') {
      collection = Events;
    } else if (productType === 'flight') {
      collection = Flights;
    } else {
      collection = Campaigns;
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { category__in: _categoryFilter, ...rest } = filters;
    const categories = await collection.getConversionCategories(model.id, {
      ...rest,
      attribution_model: attributionModel,
    });

    return categories.map((cat: string) => ({ label: cat, value: cat }));
  }, [model, attributionModel, filters, Events, Flights, Campaigns]);

  return { loadOptions };
}
